import { FunctionalMap } from "../utils/objectUtils";
import {
	lowerThan,
	upperThan,
	round,
	computePositionNoRotation,
	baseIntersect
} from "../utils/position";

const AXIS_MAPPER: { x: 0; y: 1; z: 2 } = { x: 0, y: 1, z: 2 };
const AXIS_SIZE_MAPPER: { x: "width"; y: "height"; z: "depth" } = {
	x: "width",
	y: "height",
	z: "depth"
};
const getDirBottom: (
	e: ElementPositionNoRotationType,
	axis: AxisEnum,
	moveUp: boolean
) => number = (e, axis, moveUp) => (moveUp ? e[axis] : round(e[axis] + e[AXIS_SIZE_MAPPER[axis]]));

/*
	fixCollisions moves the element if it collides with another element in content.
	Caution: element can be mutated!
	More specifically, element.position will be initialized if undefined, and element.position[1] can be mutated to fix the collisions.
	TODO: change function to return new element position rather than mutating it...
 */
export function fixCollisions(
	content: Array<string>,
	elements: FunctionalMap<string, ElementDefinitionType>,
	elementsInContainers: FunctionalMap<string, ContainerElementRef>,
	{ elementId, containerId, position }: ContainerElementRef,
	axis: AxisEnum,
	moveUp: boolean
) {
	// Use compareFunc. Was using lowerThan(...) === moveUp, but this raises issues with equalities, that must ALWAYS be rejected...
	const compareFunc = moveUp ? lowerThan : upperThan;
	const otherElementsBlocks: Array<ElementPositionNoRotationType> = [];

	content.forEach(eltId => {
		if (eltId === elementId) {
			throw new Error(
				"Content must NOT contain the element when detecting collisions. This is a bug..."
			);
		}
		const e = elements.hasAndGet(eltId);
		const ref = elementsInContainers.hasAndGet(eltId);
		e.getParts().forEach(block =>
			otherElementsBlocks.push(computePositionNoRotation(block, e, ref.position))
		);
	});

	const element = elements.hasAndGet(elementId);
	const elementBlocksPosition = element
		.getParts()
		.map(block => computePositionNoRotation(block, element, position));
	if (elementBlocksPosition.length === 0) {
		// Should never happen...
		throw new Error(`Could not find element [${elementId}] position...`);
	}

	const getElementMin = () =>
		elementBlocksPosition.reduce(
			(min, block) => (moveUp ? Math.min : Math.max)(min, getDirBottom(block, axis, moveUp)),
			moveUp ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY
		);
	const elementMinValue = getElementMin();

	// Find all elements that could overlap (orthogonal base to axis intersects, and NOT already "below" the element regarding the direction)
	const otherElements = otherElementsBlocks.filter(e => {
		const commonBaseWithAnyBlock = elementBlocksPosition.some(block =>
			baseIntersect(e, block, axis)
		);
		if (!commonBaseWithAnyBlock) {
			// The elements cannot overlap, they do not have the same base...
			return false;
		}

		// Compare position to filter out elements below
		return compareFunc(elementMinValue, getDirBottom(e, axis, !moveUp));
	});

	if (otherElements.length === 0) {
		// No collisions
		return;
	}

	// Manage collisions: move up/down (axis, up according to direction) while collisions still occur (within the limit of 1000 loops...)
	let intersecting = true;
	let intersectableElements = otherElements;
	// Method to find intersecting elements
	// It needs to return as well WHICH block intersects, for translation offset computation
	const findIntersecting: (
		intersectable: Array<ElementPositionNoRotationType>
	) => (
		res: {
			othersIntersecting: Array<ElementPositionNoRotationType>;
			blockIntersecting: ElementPositionNoRotationType | null;
		},
		block: ElementPositionNoRotationType
	) => {
		othersIntersecting: Array<ElementPositionNoRotationType>;
		blockIntersecting: ElementPositionNoRotationType | null;
	} = intersectable => ({ othersIntersecting, blockIntersecting }, block) =>
		blockIntersecting !== null && othersIntersecting.length > 0
			? { othersIntersecting, blockIntersecting }
			: {
					blockIntersecting: block,
					othersIntersecting: intersectable.filter(
						e =>
							baseIntersect(e, block, axis) &&
							compareFunc(
								getDirBottom(e, axis, moveUp),
								getDirBottom(block, axis, !moveUp)
							) &&
							compareFunc(
								getDirBottom(block, axis, moveUp),
								getDirBottom(e, axis, !moveUp)
							)
					)
			  };

	const getComparator: () => (e: ElementPositionNoRotationType) => boolean = () => {
		const min = getElementMin();
		return e => compareFunc(min, getDirBottom(e, axis, !moveUp));
	};
	// To avoid infinite loop, use counter... Max 1000 loops...
	let intersectingCounter = 0;
	while (intersecting && intersectingCounter++ < 1000) {
		// Now find elements actually intersecting
		const { othersIntersecting, blockIntersecting } = elementBlocksPosition.reduce(
			findIntersecting(intersectableElements),
			{
				othersIntersecting: [],
				blockIntersecting: null
			}
		);

		intersecting = othersIntersecting.length > 0;
		if (intersecting && blockIntersecting !== null) {
			// Find the max/min height of the elements, to move the element above/below
			const newPosValue = othersIntersecting.reduce(
				(res, e) => (moveUp ? Math.max : Math.min)(getDirBottom(e, axis, !moveUp), res),
				moveUp ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY
			);
			const delta = round(newPosValue - getDirBottom(blockIntersecting, axis, moveUp));
			console.debug(
				`Element is currently intersecting with ${
					othersIntersecting.length
				} elements... Move ${moveUp ? "up" : "down"} ${axis} by`,
				delta
			);

			if (!position.position) {
				position.position = [0, 0, 0];
			}
			const posIndex = AXIS_MAPPER[axis];
			position.position[posIndex] = round(position.position[posIndex] + delta);
			elementBlocksPosition.forEach(b => {
				b[axis] = round(b[axis] + delta);
			});
			if (!moveUp && position.position[posIndex] < 0) {
				// We are below 0, moveUp instead...
				// Reset to 0 because element has been mutated...
				position.position[posIndex] = 0;
				fixCollisions(
					content,
					elements,
					elementsInContainers,
					{ elementId, containerId, position },
					axis,
					true
				);
				return;
			}

			// Remove elements from intersectable that are now BELOW/ABOVE the Element
			intersectableElements = intersectableElements.filter(getComparator());
		}
	}
	if (intersectingCounter >= 1000) {
		console.error("Was still finding intersections... Give up and stay where we are...");
	}
}
